import { requestApiJson } from '../api/client';

const VERSION_MARKER_PATH = '/app-version.json';
const BUILD_ID_PATTERN = /^[A-Za-z0-9._-]{1,160}$/;
const VERSION_CHECK_TIMEOUT_MS = 15_000;

export const CURRENT_WEB_BUILD_ID = String(process.env.REACT_APP_WEB_BUILD_ID || '').trim();

export const normalizeWebBuildId = (value: unknown): string => {
  const normalized = String(value || '').trim();
  return BUILD_ID_PATTERN.test(normalized) ? normalized : '';
};

export const isNewerWebBuild = (currentBuildId: unknown, latestBuildId: unknown): boolean => {
  const current = normalizeWebBuildId(currentBuildId);
  const latest = normalizeWebBuildId(latestBuildId);
  return Boolean(current && latest && current !== latest);
};

export const fetchLatestWebBuildId = async (): Promise<string> => {
  const markerPath =
    `${VERSION_MARKER_PATH}?current=${encodeURIComponent(CURRENT_WEB_BUILD_ID || 'unknown')}`;
  const markerUrl = typeof window === 'undefined'
    ? markerPath
    : new URL(markerPath, window.location.origin).toString();
  const controller = new AbortController();
  const timeoutId = globalThis.setTimeout(() => controller.abort(), VERSION_CHECK_TIMEOUT_MS);
  try {
    const { data: payload, response } = await requestApiJson<{ buildId?: unknown }>(
      markerUrl,
      {
        method: 'GET',
        cache: 'no-store',
        credentials: 'same-origin',
        signal: controller.signal,
        headers: {
          Accept: 'application/json',
          'Cache-Control': 'no-cache',
        },
      },
    );
    const contentType = String(response.headers.get('content-type') || '').toLowerCase();
    if (!response.ok || !contentType.includes('application/json')) {
      throw new Error('Web app version marker was not returned as JSON.');
    }
    const buildId = normalizeWebBuildId(payload?.buildId);
    if (!buildId) throw new Error('Web app version marker is invalid.');
    return buildId;
  } finally {
    globalThis.clearTimeout(timeoutId);
  }
};

export const nextVersionCheckRetryMs = ({
  failureCount,
  baseMs,
  maxMs,
  random = Math.random,
}: {
  failureCount: number;
  baseMs: number;
  maxMs: number;
  random?: () => number;
}): number => {
  const exponent = Math.min(Math.max(Math.floor(failureCount) - 1, 0), 6);
  const bounded = Math.min(baseMs * (2 ** exponent), maxMs);
  const jitter = 0.85 + Math.min(Math.max(random(), 0), 1) * 0.3;
  return Math.round(bounded * jitter);
};
